import axios from "axios";

import { appendAppLinkAdClickIdsToUrl } from "@/lib/ad-click-ids";
import type { AppLinkAdClickKey } from "@/lib/ad-click-ids";
import type { FormContext } from "@/schemas";

import {
  APPLY_NOW_FORM_NAME,
  APPLY_NOW_HUBSPOT_FORM_GUID,
  APPLY_NOW_SIGNUP_URL,
  SIGNUP_SCREEN_HINT_PARAM,
  SIGNUP_SCREEN_HINT_VALUE,
} from "./constants";
import { mapCountryToHubSpotIbCountryIncorporatedReported } from "./funding-eligibility-countries";
import {
  getCurrencyCodeForCountryLabel,
  getHubSpotIbMonthlyReportedValue,
} from "./hubspot-revenue-mapping";
import { mapCategoryToHubSpotIndustry } from "./industry-mapping";
import { setSignupPrefillCookies } from "./signup-cookies";

export interface ApplyNowEligibilityFields {
  revenue?: string;
  category?: string;
  country?: string;
}

export interface SubmitApplyNowOptions {
  email: string;
  hubspotContext: FormContext;
  eligibility?: ApplyNowEligibilityFields;
  /** When false, submits to HubSpot but does not redirect (ineligible path). */
  redirectToSignup?: boolean;
  /**
   * Allow an empty email. HubSpot requires an email, so the submission is
   * skipped and the visitor is sent straight to signup without a prefill.
   */
  allowEmptyEmail?: boolean;
  posthogHeaders?: Record<string, string>;
  /** Ad click IDs forwarded to signup, matching ExternalLink behaviour. */
  adClickIds?: Partial<Record<AppLinkAdClickKey, string>>;
  /** Called after the HubSpot attempt, before any signup redirect. */
  onComplete?: () => void | Promise<void>;
  /** Reports a recovered HubSpot failure without blocking signup. */
  onHubSpotError?: (error: unknown) => void;
  /** Delay in ms before redirecting to signup (eligible path only). */
  redirectDelayMs?: number;
}

function parseRevenue(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const cleaned = raw.replace(/,/g, "").replace(/[^0-9.]/g, "");
  if (!cleaned) return undefined;
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export function buildHubSpotPayload(
  email: string,
  eligibility?: ApplyNowEligibilityFields
): Record<string, string> {
  const data: Record<string, string> = { email };

  const revenue = parseRevenue(eligibility?.revenue);
  const category = eligibility?.category?.trim();
  const hubspotCountry = mapCountryToHubSpotIbCountryIncorporatedReported(
    eligibility?.country ?? ""
  );

  if (hubspotCountry) {
    data["0-2/ib_country_incorporated_reported"] = hubspotCountry;
  }

  if (revenue != null && hubspotCountry) {
    const currencyCode = getCurrencyCodeForCountryLabel(hubspotCountry);
    data["0-2/ib_monthly_revenue_usd_reported"] =
      getHubSpotIbMonthlyReportedValue(revenue, currencyCode);
  }

  const industry = mapCategoryToHubSpotIndustry(category ?? "");
  if (industry) {
    data["0-2/industry"] = industry;
  }

  return data;
}

async function submitToHubSpot(
  data: Record<string, string>,
  hubspotContext: FormContext,
  headers?: Record<string, string>
): Promise<void> {
  await axios({
    url: "/api/submit-hubspot-form",
    method: "POST",
    data: {
      guid: APPLY_NOW_HUBSPOT_FORM_GUID,
      formName: APPLY_NOW_FORM_NAME,
      data,
      hubspotContext,
    },
    headers,
  });
}

export async function submitApplyNow({
  email,
  hubspotContext,
  eligibility,
  redirectToSignup = true,
  allowEmptyEmail = false,
  posthogHeaders,
  adClickIds,
  onComplete,
  onHubSpotError,
  redirectDelayMs = 0,
}: SubmitApplyNowOptions): Promise<void> {
  const trimmedEmail = email.trim();
  if (!trimmedEmail && !allowEmptyEmail) {
    throw new Error("Email is required");
  }

  // HubSpot rejects a submission without an email, so an empty submit skips
  // straight to signup rather than creating a contact.
  if (trimmedEmail) {
    const data = buildHubSpotPayload(trimmedEmail, eligibility);
    try {
      await submitToHubSpot(data, hubspotContext, posthogHeaders);
    } catch (error) {
      if (!redirectToSignup) {
        throw error;
      }
      try {
        onHubSpotError?.(error);
      } catch {
        // Error reporting must never block the signup recovery path.
      }
    }
  }
  await onComplete?.();

  if (!redirectToSignup) {
    return;
  }

  const revenue = parseRevenue(eligibility?.revenue);
  setSignupPrefillCookies({
    revenue,
    countryLabel: eligibility?.country,
  });

  const signupUrl = new URL(APPLY_NOW_SIGNUP_URL);
  if (trimmedEmail) {
    signupUrl.searchParams.set("email", trimmedEmail);
  } else {
    // No email to prefill, so nothing else signals signup intent — ask for the
    // signup screen outright rather than landing the visitor on login.
    signupUrl.searchParams.set(
      SIGNUP_SCREEN_HINT_PARAM,
      SIGNUP_SCREEN_HINT_VALUE
    );
  }
  const destination = appendAppLinkAdClickIdsToUrl(
    signupUrl.toString(),
    adClickIds ?? {}
  );

  if (redirectDelayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, redirectDelayMs));
  }

  window.location.href = destination;
}
